import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, User, FileText, Briefcase, MessageSquare, Download, CheckCircle2, XCircle, Edit3, ScrollText } from "lucide-react";
import { api, formatApiError, isApiNetworkError } from "@/lib/api";
import QuoteChat from "@/components/QuoteChat";
import NotificationBell from "@/components/NotificationBell";
import BackendServiceNotice from "@/components/BackendServiceNotice";
import SiteBrandRow from "@/components/SiteBrandRow";
import { useAuth } from "@/lib/auth";
import { useCms } from "@/lib/cms";
import { filenameFromDisposition } from "@/pages/admin/quoteLifecycle";

const QUOTE_STATUS_LABEL = {
  draft: "작성중", sent: "발송완료", viewed: "열람완료",
  approved: "승인", revision_requested: "수정요청", rejected: "반려",
};

const CONTRACT_STATUS_LABEL = QUOTE_STATUS_LABEL;

const INQUIRY_STATUS_LABEL = {
  received: "문의",
  responding: "상담중",
  answered: "상담완료",
};

const PROJECT_STATUS_LABEL = {
  planning: "기획",
  in_progress: "개발 중",
  testing: "테스트",
  live: "오픈",
  maintenance: "유지보수",
};

export default function CustomerMyPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { logout: authLogout, user: authUser } = useAuth();
  const { cms: cmsForLogo } = useCms();
  const [tab, setTab] = useState(() => searchParams.get("tab") || "profile");
  const [user, setUser] = useState(null);
  const [inquiries, setInquiries] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [chatRoom, setChatRoom] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [withdrawAgreed, setWithdrawAgreed] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);

  const logout = () => {
    authLogout();
    navigate("/login", { replace: true });
  };

  const withdraw = async () => {
    if (!withdrawAgreed || withdrawing) return;
    try {
      setWithdrawing(true);
      await api.delete("/customer/me");
      authLogout();
      toast.success("회원 탈퇴가 완료되었습니다.");
      navigate("/", { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "탈퇴 처리 중 오류가 발생했습니다.");
    } finally {
      setWithdrawing(false);
    }
  };

  const load = async () => {
    try {
      setLoadError(false);
      const [u, i, q, ct, p] = await Promise.all([
        api.get("/customer/me"),
        api.get("/customer/inquiries"),
        api.get("/customer/quotes"),
        api.get("/customer/contracts"),
        api.get("/customer/projects"),
      ]);
      setUser(u.data); setInquiries(i.data); setQuotes(q.data); setContracts(ct.data); setProjects(p.data);
    } catch (e) {
      if (e?.response?.status === 401) logout();
      else if (isApiNetworkError(e)) setLoadError(true);
      else toast.error(formatApiError(e, "데이터를 불러오지 못했습니다."));
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const switchTab = (nextTab) => {
    setTab(nextTab);
    if (nextTab === "profile") setSearchParams({});
    else setSearchParams({ tab: nextTab });
  };

  useEffect(() => {
    const urlTab = searchParams.get("tab");
    if (urlTab && urlTab !== tab) setTab(urlTab);
  }, [searchParams, tab]);

  const handleNotificationClick = (n) => {
    const cat = n?.category;
    const link = n?.link || "";
    if (cat === "quote" || link.includes("quotes")) switchTab("quotes");
    else if (cat === "contract" || link.includes("contracts")) switchTab("contracts");
    else if (cat === "inquiry" || link.includes("inquiries")) switchTab("inquiries");
    else if (cat === "project" || link.includes("projects")) switchTab("projects");
    load();
  };

  const quoteAction = async (qid, action, msg = "") => {
    try {
      await api.post(`/customer/quotes/${qid}/action`, { action, message: msg });
      toast.success("처리되었습니다.");
      load();
    } catch { toast.error("오류"); }
  };

  const contractAction = async (cid, action, msg = "") => {
    try {
      await api.post(`/customer/contracts/${cid}/action`, { action, message: msg });
      toast.success("처리되었습니다.");
      load();
    } catch { toast.error("오류"); }
  };

  const downloadQuote = async (qid) => {
    const loading = toast.loading("PDF 준비 중입니다...");
    try {
      const r = await api.get(`/customer/quotes/${qid}/pdf`, {
        responseType: "blob",
        timeout: 120000,
      });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filenameFromDisposition(r.headers?.["content-disposition"], `quote-${qid.slice(0, 8)}.pdf`);
      a.click();
      URL.revokeObjectURL(url);
      toast.success("다운로드합니다.", { id: loading });
    } catch (e) {
      let msg = "다운로드 실패. 잠시 후 다시 시도해 주세요.";
      const data = e?.response?.data;
      if (data instanceof Blob) {
        try {
          const j = JSON.parse(await data.text());
          if (j.detail) msg = j.detail;
        } catch { /* ignore */ }
      } else if (e?.response?.data?.detail) {
        msg = e.response.data.detail;
      }
      toast.error(msg, { id: loading });
    }
  };

  const downloadContract = async (cid) => {
    const loading = toast.loading("PDF 준비 중입니다...");
    try {
      const r = await api.get(`/customer/contracts/${cid}/pdf`, {
        params: { t: Date.now() },
        responseType: "blob",
        timeout: 120000,
      });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `contract-${cid.slice(0, 8)}-${Date.now()}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("다운로드합니다.", { id: loading });
    } catch {
      toast.error("다운로드 실패", { id: loading });
    }
  };

  const tabs = [
    { id: "profile", label: "내 정보", icon: User },
    { id: "inquiries", label: "문의 내역", icon: MessageSquare },
    { id: "quotes", label: "견적 내역", icon: FileText },
    { id: "contracts", label: "계약 내역", icon: ScrollText },
    { id: "projects", label: "프로젝트", icon: Briefcase },
  ];

  return (
    <div data-testid="mypage" className="min-h-screen bg-[#050505] text-white">
      <div className="border-b border-white/10 bg-black/40 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <SiteBrandRow
            cms={cmsForLogo}
            linkTo="/"
            linkTestId="mypage-logo-link"
            logoTestId="mypage-logo-image"
            suffix="마이페이지"
            iconOnly
            size="md"
          />
          <div className="flex items-center gap-3">
            <NotificationBell scope="customer" onItemClick={handleNotificationClick} />
            <span className="text-xs text-white/50 hidden sm:block">{user?.email}</span>
            <button onClick={logout} data-testid="mypage-logout" className="text-xs text-white/60 hover:text-white flex items-center gap-1.5 border border-white/10 px-3 py-1.5 rounded-full"><LogOut size={12}/>로그아웃</button>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-6 flex gap-1 overflow-x-auto">
          {tabs.map((t) => {
            const Ic = t.icon;
            return (
              <button key={t.id} onClick={()=>switchTab(t.id)} data-testid={`mp-tab-${t.id}`}
                className={`flex items-center gap-2 px-4 py-3 text-sm border-b-2 transition-colors whitespace-nowrap ${tab===t.id?"border-white text-white":"border-transparent text-white/50 hover:text-white"}`}>
                <Ic size={14}/>{t.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        <BackendServiceNotice />
        {loadError && !user ? (
          <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-8 text-sm text-white/60 leading-relaxed">
            마이페이지 데이터를 불러올 수 없습니다. 백엔드 API 연결 후 다시 이용해 주세요.
          </div>
        ) : null}
        {tab === "profile" && user && (
          <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-8">
            <h2 className="font-display text-xl mb-6">계정 정보</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <Info label="이름" value={user.name}/>
              <Info label="이메일" value={user.email}/>
              <Info label="회사" value={user.company || "-"}/>
              <Info label="연락처" value={user.phone || "-"}/>
              <Info label="가입일" value={user.created_at?.slice(0,10)}/>
            </div>
            <div className="mt-8 pt-6 border-t border-white/10">
              <h3 className="text-sm font-medium text-white/80 mb-2">회원 탈퇴</h3>
              <p className="text-xs text-white/45 mb-4 leading-relaxed">
                탈퇴하시면 계정과 마이페이지에 저장된 정보가 모두 삭제되며, 이후 복구할 수 없습니다.
              </p>
              <label className="flex items-start gap-2.5 cursor-pointer mb-4 group">
                <input
                  type="checkbox"
                  checked={withdrawAgreed}
                  onChange={(e) => setWithdrawAgreed(e.target.checked)}
                  data-testid="mypage-withdraw-agree"
                  className="mt-0.5 h-4 w-4 shrink-0 rounded border-white/20 bg-white/5 text-red-400 focus:ring-red-400/30"
                />
                <span className="text-xs text-white/55 leading-relaxed group-hover:text-white/70 transition-colors">
                  위 내용을 확인했으며, 탈퇴 시 모든 정보가 삭제되는 것에 동의합니다.
                </span>
              </label>
              <button
                type="button"
                onClick={withdraw}
                disabled={!withdrawAgreed || withdrawing}
                data-testid="mypage-withdraw"
                className="text-xs text-red-300 border border-red-500/30 px-4 py-2 rounded-full hover:bg-red-500/10 transition-colors disabled:opacity-40 disabled:pointer-events-none"
              >
                {withdrawing ? "처리 중..." : "탈퇴하기"}
              </button>
            </div>
          </div>
        )}

        {tab === "inquiries" && (
          <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl overflow-hidden">
            {inquiries.length === 0 ? <Empty text="문의 내역이 없습니다."/> :
              inquiries.map((q) => (
                <div key={q.id} className="border-b border-white/5 p-5">
                  <div className="flex items-center justify-between">
                    <div className="font-medium text-sm">{q.service_type || "문의"} - {q.company}</div>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-200 border border-blue-500/20">
                      {INQUIRY_STATUS_LABEL[q.status] || q.status}
                    </span>
                  </div>
                  <div className="text-xs text-white/60 mt-1">{q.message}</div>
                </div>
              ))}
          </div>
        )}

        {tab === "quotes" && (
          <div className="space-y-4">
            {quotes.length === 0 ? <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl"><Empty text="견적이 없습니다."/></div> :
              quotes.map((q) => (
                <div key={q.id} data-testid={`mp-quote-${q.id}`} className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-display text-lg">{q.title}</div>
                      {q.document_label ? (
                        <div className="mt-1 text-[10px] px-2 py-0.5 rounded-full border border-white/15 text-white/70 inline-block">
                          {q.customer_headline || q.document_label}
                        </div>
                      ) : null}
                      <div className="text-xs text-white/40 mt-1">총 {(q.total_amount ?? q.total ?? 0).toLocaleString()}원 · {QUOTE_STATUS_LABEL[q.status]}</div>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <button onClick={()=>downloadQuote(q.id)} className="text-xs border border-white/10 px-3 py-1.5 rounded-full hover:border-white/30 flex items-center gap-1"><Download size={12}/>PDF</button>
                      <button onClick={()=>setChatRoom(q.id)} className="text-xs border border-white/10 px-3 py-1.5 rounded-full hover:border-white/30 flex items-center gap-1"><MessageSquare size={12}/>채팅</button>
                    </div>
                  </div>
                  <div className="mt-4 border-t border-white/5 pt-4 space-y-1.5">
                    {(q.items||[]).map((it,i)=>(
                      <div key={i} className="flex justify-between text-xs gap-3">
                        <span className="text-white/70">{it.name} <span className="text-white/30">· {it.scope}</span>{it.quantity > 1 ? ` × ${it.quantity}` : ""}</span>
                        <span className="text-white">{(it.line_total || ((it.unit_price||it.amount||0) * (it.quantity||1))).toLocaleString()}원</span>
                      </div>
                    ))}
                    <div className="flex justify-between text-xs text-white/50 pt-2">
                      <span>공급가</span><span>{(q.supply_amount ?? q.total ?? 0).toLocaleString()}원</span>
                    </div>
                    <div className="flex justify-between text-xs text-white/50">
                      <span>VAT</span><span>{(q.vat_amount ?? Math.round((q.supply_amount ?? q.total ?? 0) * 0.1)).toLocaleString()}원</span>
                    </div>
                    <div className="flex justify-between text-xs text-white pt-1">
                      <span>총액</span><span>{(q.total_amount ?? Math.round((q.supply_amount ?? q.total ?? 0) * 1.1)).toLocaleString()}원</span>
                    </div>
                    {(q.unpriced_gap_items || []).length > 0 && q.pricing_kind !== "final" ? (
                      <div className="pt-3 text-xs text-white/70">
                        <div className="font-medium text-white mb-1">기술 검토 필요 / 미산정 항목</div>
                        {(q.unpriced_gap_items || []).map((item) => (
                          <div key={item}>· {item}</div>
                        ))}
                        <p className="text-[11px] text-white/45 mt-1">현재 금액에는 미산정 기술검토 항목이 포함되지 않았습니다.</p>
                      </div>
                    ) : null}
                    {(q.excluded_scope_items || []).length > 0 ? (
                      <div className="pt-3 text-xs text-white/70">
                        <div className="font-medium text-white mb-1">이번 견적에서 제외된 요청</div>
                        {(q.excluded_scope_items || []).map((item, idx) => (
                          <div key={idx}>· {item.customer_label || item.label || item} — 이번 구축 범위에서 제외</div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                  {!["approved","rejected"].includes(q.status) && q.can_approve !== false && (
                    <div className="mt-5 flex flex-wrap gap-2">
                      <button onClick={()=>quoteAction(q.id,"approve")} data-testid={`mp-approve-${q.id}`} className="text-xs bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 px-3 py-1.5 rounded-full flex items-center gap-1"><CheckCircle2 size={12}/>승인</button>
                      <button onClick={()=>{const m=prompt("수정 요청 내용:");if(m)quoteAction(q.id,"request_revision",m);}} className="text-xs border border-white/10 px-3 py-1.5 rounded-full flex items-center gap-1"><Edit3 size={12}/>수정 요청</button>
                      <button onClick={()=>{if(window.confirm("정말 반려하시겠습니까?"))quoteAction(q.id,"reject");}} className="text-xs text-red-400 border border-red-500/30 px-3 py-1.5 rounded-full flex items-center gap-1"><XCircle size={12}/>반려</button>
                    </div>
                  )}
                  {!["approved","rejected"].includes(q.status) && q.can_approve === false ? (
                    <p className="mt-4 text-[12px] text-amber-200">이 견적은 임시 견적이라 최종 승인할 수 없습니다. 기술 검토 후 최종 견적이 도착하면 승인해 주세요.</p>
                  ) : null}
                </div>
              ))}
          </div>
        )}

        {tab === "contracts" && (
          <div className="space-y-4">
            {contracts.length === 0 ? <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl"><Empty text="계약서가 없습니다."/></div> :
              contracts.map((c) => (
                <div key={c.id} data-testid={`mp-contract-${c.id}`} className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-display text-lg">{c.title}</div>
                      <div className="text-xs text-white/40 mt-1">{(c.contract_amount || 0).toLocaleString()}원 · {CONTRACT_STATUS_LABEL[c.status]}</div>
                    </div>
                    <button onClick={() => downloadContract(c.id)} className="text-xs border border-white/10 px-3 py-1.5 rounded-full hover:border-white/30 flex items-center gap-1"><Download size={12}/>PDF</button>
                  </div>
                  {!["approved", "rejected"].includes(c.status) && (
                    <div className="mt-5 flex flex-wrap gap-2">
                      <button onClick={() => contractAction(c.id, "approve")} className="text-xs bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 px-3 py-1.5 rounded-full flex items-center gap-1"><CheckCircle2 size={12}/>승인</button>
                      <button onClick={() => { const m = prompt("수정 요청 내용:"); if (m) contractAction(c.id, "request_revision", m); }} className="text-xs border border-white/10 px-3 py-1.5 rounded-full flex items-center gap-1"><Edit3 size={12}/>수정 요청</button>
                      <button onClick={() => { if (window.confirm("정말 반려하시겠습니까?")) contractAction(c.id, "reject"); }} className="text-xs text-red-400 border border-red-500/30 px-3 py-1.5 rounded-full flex items-center gap-1"><XCircle size={12}/>반려</button>
                    </div>
                  )}
                </div>
              ))}
          </div>
        )}

        {tab === "projects" && (
          <div className="space-y-4">
            {projects.length === 0 ? <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl"><Empty text="진행 중 프로젝트가 없습니다."/></div> :
              projects.map((p) => (
                <div key={p.id} className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6">
                  <div className="flex justify-between mb-2">
                    <div className="font-display text-lg">{p.title}</div>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-200 border border-emerald-500/20">
                      {PROJECT_STATUS_LABEL[p.status] || p.status}
                    </span>
                  </div>
                  <div className="w-full h-2 bg-white/5 rounded-full overflow-hidden">
                    <div className="h-full bg-[#0055FF]" style={{width:`${p.progress||0}%`}}/>
                  </div>
                  <div className="text-xs text-white/40 mt-2">진행률 {p.progress||0}%</div>
                </div>
              ))}
          </div>
        )}
      </div>

      {chatRoom && (
        <QuoteChat
          roomId={chatRoom}
          onClose={() => setChatRoom(null)}
          quoteTitle={quotes.find((q) => q.id === chatRoom)?.title}
        />
      )}
    </div>
  );
}

function Info({label,value}){return(<div><div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1">{label}</div><div>{value}</div></div>);}
function Empty({text}){return <div className="p-12 text-center text-white/40 text-sm">{text}</div>;}
